import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SyncCenterStateService } from '@modules/shared/sync-center/sync-center-state.service';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { videoUploadsActions } from '@shared/store/video-uploads/video-uploads.actions';
import { selectHasInterruptedOrErroredVideoUploads } from '@shared/store/video-uploads/video-uploads.selectors';
import { filter, take, withLatestFrom } from 'rxjs';

// Only place in the store allowed to reach into the sync-center module. Owns the decision of when
// to auto-open the panel — the store dispatches actions as usual and never calls this directly.
@Injectable({ providedIn: 'root' })
export class SyncCenterBridgeService {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly syncCenterState = inject(SyncCenterStateService);

  constructor() {
    this.actions$
      .pipe(
        ofType(videoUploadsActions.uploadSequenceVideos, videoUploadsActions.resumeVideoUpload),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.syncCenterState.open());

    // On load/login the store hydrates interrupted sessions; open the panel so the user sees
    // anything that needs their attention (interrupted or errored uploads). Once only: the sessions
    // of every client the user works with land as separate actions, and the licenses (hence the
    // sessions) are reloaded on unrelated events — without `take(1)` the panel would keep springing
    // back open after the user closed it.
    this.actions$
      .pipe(
        ofType(videoUploadsActions.loadUploadSessionsSuccess),
        withLatestFrom(this.store.select(selectHasInterruptedOrErroredVideoUploads)),
        filter(([, hasBlockedUploads]) => hasBlockedUploads),
        take(1),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.syncCenterState.open());
  }
}
